import type { DropdownMultiselectOptionProps } from './types'
import { space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Checkbox, focusWithinOutline, resetFocusOutline, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'
import { DropdownMultiselectContext } from './context'

const Wrapper = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${space('primitives_space_8')};
  cursor: pointer;
  min-height: ${tokPx('semantics_controls_m_min_height')};
  border-radius: ${tokPx('semantics_controls_m_corner_radius')};

  ${focusWithinOutline()}
  ${Checkbox} {
    ${resetFocusOutline()}
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`

const TextWrapper = styled.div<{ $vertical: boolean }>`
  display: flex;
  flex: 1 1 0;
  gap: ${space('primitives_space_4')};
  margin-left: ${space('primitives_space_8')};

  ${({ $vertical }) =>
    $vertical
      ? `
    flex-direction: column;
    align-items: flex-start;
  `
      : `
    flex-direction: row;
    align-items: center;
  `}
`

const Label = styled(Typography)`
  align-self: stretch;
  display: flex;
  flex: 1 1 0;
`

const SupportingText = styled(Label)`
  color: ${tok('semantics_content_secondary_color')};
`

interface CheckboxWithExtraProps extends DropdownMultiselectOptionProps {
  'description'?: string
  'count'?: number | string
  'supportingText'?: string
  'data-qa'?: string
}

function CheckboxWithExtra({ value, children, count, supportingText, ...rest }: CheckboxWithExtraProps) {
  const { selectedValues, removeOption, addOption } = React.use(DropdownMultiselectContext)

  const isSelected = selectedValues.includes(value)

  const handleCheckedChange = () => {
    if (isSelected) {
      removeOption(value)
      return
    }

    addOption(value)
  }

  return (
    <Wrapper {...rest}>
      <Checkbox checked={isSelected} onCheckedChange={handleCheckedChange} />
      <TextWrapper $vertical={Boolean(supportingText)}>
        <Label>{children}</Label>
        {count && <Typography variant="body-m-regular-tight">{count}</Typography>}
        {supportingText && <SupportingText variant="body-s-regular-tight">{supportingText}</SupportingText>}
      </TextWrapper>
    </Wrapper>
  )
}

export function CheckboxWithCounterOption({ ...rest }: CheckboxWithExtraProps) {
  return <CheckboxWithExtra {...rest} />
}
export function CheckboxWithSupportingTextOption({ ...rest }: CheckboxWithExtraProps) {
  return <CheckboxWithExtra {...rest} />
}
